/**
 * RawScore — the arcade score. Not normalized: harder levels, faster answers and
 * long streaks all pay more. Every part is rounded to a whole point so the run
 * total is an integer the server can compare exactly.
 * Spec: docs/GAME_DESIGN.md § 5; rationale: docs/SCORING.md.
 */
import { SCORING } from '../config.ts';
import { getLevel } from '../levels/levels.ts';
import { clamp01 } from '../math/stats.ts';
import type { ScoreContext } from '../types.ts';
import type { ScoringConstants } from './types.ts';

/** Stable labels for ScoreDelta.parts (UI floating text keys). */
export type ScorePartLabel = 'correct' | 'layerBreak' | 'cleanFloor' | 'levelUp';

/** streakMult = 1 + STEP × min(streak − 1, CAP); 1 for the first answer of a streak. */
export function streakMultiplier(streak: number, k: ScoringConstants = SCORING): number {
  return 1 + k.RAW_STREAK_STEP * Math.min(Math.max(streak - 1, 0), k.RAW_STREAK_CAP);
}

/** Raw parts for one answer; empty for wrong answers and timeouts. */
export function rawParts(ctx: ScoreContext, k: ScoringConstants = SCORING): { label: ScorePartLabel; raw: number }[] {
  const { record } = ctx;
  if (record.outcome !== 'correct') return [];
  const D = record.difficultyIndex;
  const speed = 1 + clamp01(record.remainingFraction);
  const parts: { label: ScorePartLabel; raw: number }[] = [
    { label: 'correct', raw: Math.round(k.RAW_CORRECT * D * speed * streakMultiplier(ctx.streak, k)) },
  ];
  if (ctx.brokeLayer) parts.push({ label: 'layerBreak', raw: Math.round(k.RAW_LAYER_BREAK * D) });
  if (ctx.bricksBefore === 0) parts.push({ label: 'cleanFloor', raw: Math.round(k.RAW_CLEAN_FLOOR * D) });
  return parts;
}

/** Raw bonus when a checkpoint raises the level: RAW_LEVEL_UP × D(new level) per level gained (0 if not raised). */
export function levelUpBonus(levelBefore: number, levelAfter: number, k: ScoringConstants = SCORING): number {
  const gained = levelAfter - levelBefore;
  if (gained <= 0) return 0;
  return Math.round(k.RAW_LEVEL_UP * getLevel(levelAfter).difficultyIndex * gained);
}
